import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, ReplaySubject, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { WebSocketService } from '../auth/web-socket.service';
import { config } from '../config';
import { Conversation, ConversationFilters } from '../models/conversation.model';
import { Message, MessageSenderType, MessageStatus } from '../models/message.model';
import { AuthService } from '../auth/auth.service';
import { IdValidatorService } from '../shared/services/id-validator.service';

// Interface for API response structure
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
}

// Constants to avoid hardcoded strings
export const CONVERSATION_CONSTANTS = {
  STATUS: {
    ACTIVE: 'Active', // Changed from OPEN
    ARCHIVED: 'Archived',
    DELETED: 'Deleted',
    ALL: 'All'
  },
  ENDPOINTS: {
    CONVERSATIONS: '/api/conversations',
    MESSAGES: '/api/messages',
  }
};

@Injectable({
  providedIn: 'root'
})
export class ConversationService {
  private apiUrl = config.apiUrl;
  
  // BehaviorSubjects to track active conversation state
  private activeConversationSubject = new BehaviorSubject<Conversation | null>(null);
  public activeConversation$ = this.activeConversationSubject.asObservable();

  // Subject to emit updated conversations
  private conversationUpdatedSource = new Subject<Conversation>();
  public conversationUpdated$ = this.conversationUpdatedSource.asObservable();

  // Subject to emit new messages received via websocket or created locally
  // Using ReplaySubject(1) to ensure late subscribers get the most recent message
  private newMessageSubject = new ReplaySubject<{conversationId: string, messageId: string, action: string, text?: string}>(1);
  public newMessage$ = this.newMessageSubject.asObservable().pipe(
    tap(event => console.log('%c [ConversationService] Message event emitted:', 'background: #e67e22; color: white; padding: 2px 5px; border-radius: 2px;', event))
  );
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private idValidator: IdValidatorService,
    private wsService: WebSocketService
  ) {
    // Setup websocket listeners immediately and also after delay for safety
    this.setupWebSocketListeners();
    setTimeout(() => this.setupWebSocketListeners(), 1000);
  }
  
  // Track if WebSocket listeners are already set up to prevent duplicates
  private webSocketListenersSetup = false;

  /**
   * Setup listeners for websocket events related to messages
   */
  private setupWebSocketListeners(): void {
    // Prevent duplicate subscriptions
    if (this.webSocketListenersSetup) {
      console.log('%c [ConversationService] WebSocket listeners already set up, skipping', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;');
      return;
    }

    try {
      console.log('%c [ConversationService] Setting up WebSocket listeners', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;');
      
      // Subscribe to websocket messages
      this.wsService.messages$.subscribe({
        next: (message: any) => {
          console.log('%c [ConversationService] Received WebSocket message:', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;', message);
          
          // Handle logout events
          if ('logout' === message.type) {
            console.log('%c [ConversationService] 🚨 Logout event received via WebSocket 🚨', 'background: #FF0000; color: #ffffff; font-size: 14px; padding: 5px;');
            
            const currentUser = this.authService.currentUserValue;
            if (currentUser && message['user_id'] === currentUser.sub) {
              console.log('%c [ConversationService] Logout event matches current user, triggering logout via AuthService', 'background: #FF4500; color: #ffffff; font-size: 12px; padding: 3px;');
              
              // Show appropriate message based on logout reason
              let logoutMessage = 'Your session has ended.';
              if (message['reason'] === 'admin_initiated') {
                logoutMessage = 'Your session was terminated by an administrator.';
              } else if (message['reason'] === 'user_initiated') {
                logoutMessage = 'You have been logged out from another session.';
              } else if (message['reason'] === 'token_expired') {
                logoutMessage = 'Your session has expired.';
              }
              
              console.log(`%c [ConversationService] ${logoutMessage}`, 'background: #FF0000; color: #ffffff; padding: 5px;');
              
              // Trigger logout via AuthService (don't send WebSocket event to prevent loops)
              this.authService.logout(false);
            } else {
              console.log('%c [ConversationService] Logout event is for different user, ignoring', 'background: #FFA500; color: #ffffff; font-size: 12px; padding: 3px;');
            }
            return;
          }
          
          // Check if this is a message event
          if ('message' === message.type) {
            console.log('%c [ConversationService] Received message event', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;', {
              messageId: message.message_id,
              conversationId: message.conversation_id,
              companyId: message.company_id
            });
            
            // Check if the message is for the current user's company
            const currentUser = this.authService.currentUserValue;
            console.log('%c [ConversationService] Current user:', 'background: #9b59b6; color: white; padding: 2px 5px; border-radius: 2px;', {
              userId: currentUser?.sub,
              companyId: currentUser?.company_id
            });
            
            // Always emit the message event regardless of company_id for debugging
            if (message.conversation_id && message.message_id) {
              console.log('%c [ConversationService] Emitting new message event', 'background: #e67e22; color: white; padding: 2px 5px; border-radius: 2px;', {
                conversationId: message.conversation_id,
                messageId: message.message_id
              });
              
              this.newMessageSubject.next({
                conversationId: message.conversation_id,
                messageId: message.message_id,
                action: 'created'
              });
            }
          }
          
          // Check if this is a conversation status update event
          if ('conversation' === message.type) {
            console.log('%c [ConversationService] Received conversation event', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;', {
              event: message.event,
              conversationId: message.conversation_id,
              status: message.status,
              companyId: message.company_id
            });
            
            if (message.conversation_id && message.event === 'status_updated') {
              console.log('%c [ConversationService] Processing conversation status update', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', {
                conversationId: message.conversation_id,
                status: message.status
              });
              
              // Use centralized update method for WebSocket status changes
              this.broadcastConversationUpdate({
                id: message.conversation_id,
                status: message.status
              }, 'websocket-status');
            }
          }
        },
        error: (error: Error) => {
          console.error('%c [ConversationService] WebSocket error:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', error);
        }
      });
      
      // Mark that WebSocket listeners are now set up
      this.webSocketListenersSetup = true;
      console.log('%c [ConversationService] WebSocket listeners setup complete', 'background: #27ae60; color: white; padding: 2px 5px; border-radius: 2px;');
      
    } catch (error: unknown) {
      console.error('%c [ConversationService] Error setting up WebSocket listeners:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', error);
      // Reset the flag so we can try again
      this.webSocketListenersSetup = false;
    }
  }

  // Get a single conversation by ID
  public getConversationById(conversationId: string): Observable<Conversation> {
    return this.http.get<ApiResponse<Conversation>>(`${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}/${conversationId}`)
      .pipe(
        map(response => {
          // Check if the response contains data
          if (response && response.success && response.data) {
            const conversation = response.data;
            
            // Convert string dates to Date objects
            if (conversation.lastMessage && conversation.lastMessage.datetime) {
              conversation.lastMessage.datetime = new Date(conversation.lastMessage.datetime);
            }
            
            // Ensure contact property exists
            if (!conversation.contact) {
              // Try to extract contact info from metadata if available
              let contactName = 'Unknown Contact';
              let contactMobile = 'Unknown';
              
              if (conversation.metadata) {
                try {
                  const metadata = JSON.parse(conversation.metadata);
                  if (metadata.contact) {
                    contactName = metadata.contact.name || 'Unknown Contact';
                    contactMobile = metadata.contact.mobile || metadata.contact.phone || 'Unknown';
                  }
                } catch (e) {
                  console.error('Error parsing conversation metadata:', e);
                }
              }
              
              conversation.contact = {
                id: '',
                mobile: contactMobile,
                name: contactName
              };
            }
            
            return conversation as Conversation;
          } else if (response && Array.isArray(response)) {
            // Handle direct response format
            const conversation = response[0] as any;
            if (conversation) {
              // Convert and ensure contact exists
              if (conversation.lastMessage && conversation.lastMessage.datetime) {
                conversation.lastMessage.datetime = new Date(conversation.lastMessage.datetime);
              }
              
              if (!conversation.contact) {
                conversation.contact = {
                  id: '',
                  mobile: 'Unknown',
                  name: 'Unknown Contact'
                };
              }
              
              return conversation as Conversation;
            }
          }
          
          throw new Error('Conversation not found');
        })
      );
  }

  // Get all conversations with optional filters
  public getConversations(filters?: ConversationFilters): Observable<Conversation[]> {
    const params: string[] = [];
    
    if (filters?.status && CONVERSATION_CONSTANTS.STATUS.ALL !== filters.status) {
      params.push(`status=${filters.status}`);
    }
    
    if (filters?.contact_id) {
      params.push(`contact_id=${filters.contact_id}`);
    }
    
    const queryParams = params.length > 0 ? `?${params.join('&')}` : '';
    
    return this.http.get<ApiResponse<Conversation[]>>(`${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}${queryParams}`)
      .pipe(
        map(response => {
          // Check if the response contains data
          if (response && Array.isArray(response)) {
            // Direct array response (old format)
            return response.map((conversation: any) => {
              // Convert string dates to Date objects
              if (conversation.lastMessage && conversation.lastMessage.datetime) {
                conversation.lastMessage.datetime = new Date(conversation.lastMessage.datetime);
              }
              
              // Ensure contact property exists
              if (!conversation.contact) {
                conversation.contact = {
                  id: '',
                  mobile: 'Unknown',
                  name: 'Unknown Contact'
                };
              }
              
              return conversation as Conversation;
            });
          } else if (response && response.success && response.data) {
            // ApiResponse format
            return response.data.map((conversation: any) => {
              // Convert string dates to Date objects
              if (conversation.lastMessage && conversation.lastMessage.datetime) {
                conversation.lastMessage.datetime = new Date(conversation.lastMessage.datetime);
              }
              
              // Log the conversation for debugging
              console.log('Server conversation:', conversation);
              
              // Ensure contact property exists
              if (!conversation.contact) {
                // Try to extract contact info from metadata if available
                let contactName = 'Unknown Contact';
                let contactMobile = 'Unknown';
                
                if (conversation.metadata) {
                  try {
                    const metadata = JSON.parse(conversation.metadata);
                    if (metadata.contact) {
                      contactName = metadata.contact.name || 'Unknown Contact';
                      contactMobile = metadata.contact.mobile || 'Unknown';
                    }
                  } catch (e) {
                    console.error('Error parsing conversation metadata:', e);
                  }
                }
                
                conversation.contact = {
                  id: '',
                  mobile: contactMobile,
                  name: contactName
                };
              }
              
              return conversation as Conversation;
            });
          }
          // Return empty array if no data
          return [];
        })
      );
  }
  
  // Get conversations for a specific contact
  public getConversationsByContactId(contactId: string): Observable<Conversation[]> {
    return this.getConversations({ contact_id: contactId });
  }

  // Get messages for a specific conversation
  public getMessages(conversationId: string): Observable<Message[]> {
    // Use the correct endpoint format for the server
    return this.http.get<any>(`${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.MESSAGES}/conversation/${conversationId}`)
      .pipe(
        map(response => {
          // Check if the response contains data
          if (response && Array.isArray(response)) {
            // Direct array response (old format)
            return response.map((message: any) => {
              return this.mapServerMessageToClientModel(message);
            });
          } else if (response && response.success && response.data) {
            // ApiResponse format
            return response.data.map((message: any) => {
              return this.mapServerMessageToClientModel(message);
            });
          }
          // Return empty array if no data
          return [];
        })
      );
  }

  // Send a new message (with optional email subject for email channels)
  public sendMessage(conversationId: string, text: string, emailSubject?: string): Observable<Message> {
    // Get the current user to use as the sender
    const currentUser = this.authService.currentUserValue;
    
    if (!currentUser || !currentUser.sub) {
      return throwError(() => new Error('User must be logged in to send a message'));
    }
    
    // Create a temporary client-side message object
    // This will be displayed immediately in the UI with 'sending' status
    const clientMessage: Message = {
      id: 'temp-' + Date.now(),
      conversationId: conversationId,
      text: text,
      messageType: 'Text',
      datetime: new Date(),
      sender: {
        type: 'User',
        id: currentUser.sub,
        name: currentUser.name
      },
      status: 'sending', // Initial status is 'sending'
      emailSubject: emailSubject || undefined
    };

    // Emit the temporary message immediately for better UX
    // This ensures the message appears in the UI right away
    setTimeout(() => {
      this.newMessageSubject.next({
        messageId: clientMessage.id,
        conversationId: conversationId,
        action: 'created',
        text: text // Include the message text so it can be displayed immediately
      });
    }, 0);
    
    // Build message payload
    const messagePayload: any = {
      conversation_id: conversationId,
      content: text,  // Use 'content' instead of 'text' to match the server model
      sender_id: currentUser.sub,
      sender_type: 'User',  // Indicate this is from a user, not a contact
      message_type: 'Text',  // Default to Text message type
      status: 'Sent'  // Required field - default status for new messages
    };

    // For email channels, include subject in metadata
    if (emailSubject) {
      messagePayload.metadata = JSON.stringify({ subject: emailSubject });
    }

    // Send the message to the server
    // and then updates it with the server response
    return this.http.post<any>(`${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.MESSAGES}`, messagePayload).pipe(
      map(response => {
        console.log('Server response:', response);
        if (response && response.success && response.data) {
          // Check if the response data is just a string ID (which is the case for message creation)
          if (typeof response.data === 'string') {
            console.log('Response contains only ID, updating client message with permanent ID');
            // Update the temporary client message with the permanent ID and 'sent' status
            return {
              ...clientMessage,
              id: response.data,
              // This branch handles `data` being a bare id STRING, so there is
              // no status on it to read - the expression that looked like it
              // read one was always 'Sent'. The optimistic local status is what
              // we have here, and the message list refreshes from the server.
              status: 'Sent' as MessageStatus
            };
          } else {
            // If the response contains a full message object, map it
            const serverMessage = this.mapServerMessageToClientModel(response.data);
            console.log('Mapped server message:', serverMessage);
            
            // Ensure the text field is populated
            if (!serverMessage.text && serverMessage.id) {
              console.log('Server message missing text, using original text:', text);
              serverMessage.text = text;
            }
            
            // Update status to 'sent' when server confirms
            // The SERVER's status, not a hardcoded 'Sent'. This overwrote
            // whatever came back, so a message the server refused to send -
            // recorded as Failed - still rendered a delivered tick.
            serverMessage.status = (response.data?.status as MessageStatus) ?? 'Sent';
            
            return serverMessage;
          }
        }
        // If there's an issue with the response, still return the client message
        return clientMessage;
      })
    );
  }

  // Helper method to map server message format to client model
  private mapServerMessageToClientModel(serverMessage: any): Message {
    // Convert string dates to Date objects - use created_at from server
    let messageDateTime = new Date();
    if (serverMessage.created_at) {
      messageDateTime = new Date(serverMessage.created_at);
    } else if (serverMessage.datetime) {
      messageDateTime = new Date(serverMessage.datetime);
    }
    
    // Log the server message for debugging with detailed structure
    console.log('Server message (raw):', JSON.stringify(serverMessage, null, 2));
    console.log('Server message fields:', {
      id: serverMessage.id,
      conversation_id: serverMessage.conversation_id,
      sender_id: serverMessage.sender_id,
      sender_type: serverMessage.sender_type,
      content: serverMessage.content,
      text: serverMessage.text,
      message_type: serverMessage.message_type,
      created_at: serverMessage.created_at,
      datetime: serverMessage.datetime
    });
    
    // Handle different message formats from the server
    let senderId = '';
    let senderType = 'User';
    let senderName: string | undefined = undefined;
    
    // Check if the message has a nested sender object (new format)
    if (serverMessage.sender) {
      senderId = serverMessage.sender.id || '';
      senderType = serverMessage.sender.type || 'User';
      senderName = serverMessage.sender.name;
    } else {
      // Handle flat structure with sender_id and sender_type (server format)
      senderId = serverMessage.sender_id || '';
      // Use the sender_type directly from the server response if available
      if (serverMessage.sender_type) {
        senderType = serverMessage.sender_type;
      } else {
        // Fallback to direction-based logic if sender_type is not available
        senderType = (false === (serverMessage.direction === 'outbound')) ? 'Contact' : 'User';
      }
    }
    
    // Create a client-side message object
    const clientMessage: Message = {
      id: serverMessage.id || '',
      conversationId: serverMessage.conversation_id || '',
      // Prioritize content over text field since the server uses content
      text: serverMessage.content || serverMessage.text || '',
      messageType: serverMessage.message_type || 'Text',
      datetime: messageDateTime,
      sender: {
        type: senderType as MessageSenderType,
        id: senderId,
        name: senderName
      },
      // Was never copied, so every message loaded from history had
      // status === undefined and the delivery ticks never rendered.
      status: serverMessage.status as MessageStatus
    };
    
    // Parse email metadata to extract subject (once, not per render)
    if (serverMessage.metadata) {
      clientMessage.metadata = serverMessage.metadata;
      try {
        const meta = JSON.parse(serverMessage.metadata);
        if (meta.subject) {
          clientMessage.emailSubject = meta.subject;
        }
      } catch {
        // ignore parse errors
      }
    }

    // Log the mapped client message for debugging
    console.log('Mapped client message:', clientMessage);
    return clientMessage;
  }

  // Update conversation status
  public updateConversationStatus(conversationId: string, newStatus: string): Observable<Conversation> {
    return this.http.patch<ApiResponse<Conversation | null>>(
      `${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}/${conversationId}`,
      { id: conversationId, status: newStatus } // Server seems to accept id in body, newStatus is the target status
    ).pipe(
      map(response => {
        if (!response || !response.success) {
          const errorMessage = response?.message || 'Failed to update conversation status due to unknown server error.';
          console.error(`[ConversationService] API error updating status for ${conversationId} to ${newStatus}:`, errorMessage);
          throw new Error(errorMessage);
        }

        // response.success is true. Server currently returns data: null.
        let conversationDataForEvents: Partial<Conversation>;

        if (response.data) {
          // Ideal case: server returns the full updated conversation in response.data
          conversationDataForEvents = response.data;
          console.log(`[ConversationService] Status updated for ${conversationId} to ${newStatus}. Server returned full data.`, response.data);
        } else {
          // Current case: server returns data: null. Construct a partial update.
          conversationDataForEvents = {
            id: conversationId,
            status: newStatus as Conversation['status']
          };
          console.log(`[ConversationService] Status updated for ${conversationId} to ${newStatus}. Server returned no data, created partial update.`, conversationDataForEvents);
        }

        // Use centralized update method for manual status changes
        this.broadcastConversationUpdate(conversationDataForEvents as Conversation, 'manual-status-change');
        
        // Return the (potentially partial) conversation data for the direct caller (ConversationComponent)
        // This ensures ConversationComponent can update its local 'conversation.status'
        return conversationDataForEvents as Conversation;
      }),
      catchError(error => {
        console.error(`[ConversationService] Error in updateConversationStatus pipe for ${conversationId} to ${newStatus}:`, error);
        return throwError(() => (error instanceof Error ? error : new Error('Unknown error during status update pipe')));
      })
    );
  }

  // Set the active conversation
  public setActiveConversation(conversation: Conversation | null): void {
    this.activeConversationSubject.next(conversation);
  }
  public createConversation(channelId: string, contact: { mobile: string, name?: string }): Observable<Conversation> {
    // Generate a title based on contact information
    const title = contact.name ? 
      `Conversation with ${contact.name}` : 
      `Conversation with ${contact.mobile}`;
    
    // Get the current user from the auth service
    const currentUser = this.authService.currentUserValue;
    
    if (!currentUser) {
      throw new Error('User must be logged in to create a conversation');
    }
    
    // Get IDs from the user object (they should be validated during login)
    const companyId = currentUser.company_id;
    const userId = currentUser.sub;
    
    // Final validation check - this should rarely fail since IDs are validated during login
    if (!this.idValidator.isValidMongoId(companyId)) {
      console.error('Invalid company ID format:', companyId);
      return throwError(() => new Error('Invalid company ID format. Expected a 24-character hexadecimal string.'));
    }
    
    if (!this.idValidator.isValidMongoId(userId)) {
      console.error('Invalid user ID format:', userId);
      return throwError(() => new Error('Invalid user ID format. Expected a 24-character hexadecimal string.'));
    }
    
    console.log('Creating conversation with company ID:', companyId, 'and user ID:', userId);
    
    return this.http.post<any>(`${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}`, {
      title,
      initiator_id: userId, // Use a proper MongoDB ID format
      initiator_type: 'User',
      channel_id: channelId, // Use the correct property name expected by the server
      external_ref: contact.mobile,
      company_id: companyId, // Use the user's actual company ID
      // Include optional fields
      tags: [],
      metadata: JSON.stringify({ contact, userEmail: currentUser.email }) // Store contact info and email in metadata
    }).pipe(
      map(response => {
        // Log the full response for debugging
        console.log('Server response for conversation creation:', response);
        
        if (response.success && response.data) {
          // Ensure the conversation has all required properties
          const conversation = response.data as any;
          
          // Log the conversation object
          console.log('Conversation data from server:', conversation);
          
          // The server might be returning the ID in a different format than expected
          // MongoDB typically returns _id, but our model expects id
          if (!conversation.id && conversation._id) {
            conversation.id = conversation._id;
            console.log('Using _id as id:', conversation.id);
          }
          
          // Map snake_case to camelCase for channelId if needed
          if (!conversation.channelId && conversation.channel_id) {
            conversation.channelId = conversation.channel_id;
            console.log('Mapped channel_id to channelId:', conversation.channelId);
          }
          
          // Check if id exists and is valid
          if (!conversation.id) {
            console.error('Server returned conversation without ID:', conversation);
            throw new Error('Server returned invalid conversation data');
          }
          
          // Create a proper Conversation object that matches our client-side model
          const clientConversation: Conversation = {
            id: conversation.id,
            title: conversation.title,
            initiator_id: conversation.initiator_id,
            initiator_type: conversation.initiator_type,
            channelId: conversation.channel_id || conversation.channelId, // Use either format
            channel_id: conversation.channel_id,
            external_ref: conversation.external_ref,
            company_id: conversation.company_id,
            status: (conversation.status || CONVERSATION_CONSTANTS.STATUS.ACTIVE) as any,
            priority: conversation.priority as any,
            tags: conversation.tags,
            metadata: conversation.metadata,
            created_at: conversation.created_at,
            updated_at: conversation.updated_at,
            // Add client-side properties
            contact: {
              id: (contact as any).id || '',
              mobile: contact.mobile,
              name: contact.name
            }
          };
          
          console.log('Created client-side conversation object:', clientConversation);
          
          // Return the properly formatted conversation object
          return clientConversation;
        }
        throw new Error((response.message as string) || 'Failed to create conversation');
      })
    );
  }

  // Update conversation metadata
  public updateConversationMetadata(conversationId: string, metadata: string): Observable<Conversation> {
    return this.http.patch<ApiResponse<Conversation | null>>(
      `${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}/${conversationId}`,
      { metadata: metadata }
    ).pipe(
      map(response => {
        if (!response || !response.success) {
          const errorMessage = response?.message || 'Failed to update conversation metadata due to unknown server error.';
          console.error(`[ConversationService] API error updating metadata for ${conversationId}:`, errorMessage);
          throw new Error(errorMessage);
        }

        // Construct a partial update if server doesn't return full data
        const conversationDataForEvents: Partial<Conversation> = response.data || {
          id: conversationId,
          metadata: metadata
        };

        console.log(`[ConversationService] Metadata updated for ${conversationId}`);
        
        // Return the (potentially partial) conversation data
        return conversationDataForEvents as Conversation;
      }),
      catchError(error => {
        console.error(`[ConversationService] Error in updateConversationMetadata for ${conversationId}:`, error);
        return throwError(() => (error instanceof Error ? error : new Error('Unknown error during metadata update')));
      })
    );
  }

  // Centralized conversation update method - handles all update sources
  public broadcastConversationUpdate(conversationUpdate: Partial<Conversation>, source: string = 'unknown'): void {
    console.log(`[ConversationService] Broadcasting conversation update from ${source}:`, conversationUpdate);
    
    // Ensure we have an ID
    if (!conversationUpdate.id) {
      console.warn('[ConversationService] Cannot broadcast update without conversation ID');
      return;
    }
    
    // Emit to all subscribers (conversation lists, active conversations, etc.)
    this.conversationUpdatedSource.next(conversationUpdate as Conversation);
    
    // Update active conversation if this update is for the currently active one
    const currentActive = this.activeConversationSubject.value;
    if (currentActive && currentActive.id === conversationUpdate.id) {
      console.log('[ConversationService] Updating active conversation with new data');
      const updatedActive = { 
        ...currentActive, 
        ...conversationUpdate  // Merge the updates into the current active conversation
      };
      this.activeConversationSubject.next(updatedActive);
    }
  }

  // Emit conversation update for local broadcasting (e.g., contact info changes)
  public emitConversationUpdate(conversation: Conversation): void {
    this.broadcastConversationUpdate(conversation, 'local-edit');
  }

  // Get the current active conversation
  public getActiveConversation(): Conversation | null {
    return this.activeConversationSubject.value;
  }

  // Mark a conversation as read by resetting its unread_count to 0
  public markConversationAsRead(conversationId: string): void {
    // Optimistically clear the badge in the UI immediately
    this.broadcastConversationUpdate({ id: conversationId, unread_count: 0 } as Partial<Conversation>, 'mark-read');

    // Persist to server (fire-and-forget)
    this.http.patch<any>(
      `${this.apiUrl}${CONVERSATION_CONSTANTS.ENDPOINTS.CONVERSATIONS}/${conversationId}`,
      { unread_count: 0 }
    ).subscribe({
      error: (err) => console.error('[ConversationService] Failed to mark conversation as read:', err)
    });
  }
}
